import React, { Component } from "react";
import { toast } from "react-toastify";
import CourseCatalog from "../../training-selection/course-catalog";
import ConfigureTrainingModal from "../../training-selection/configure-training-modal";
import { DEFAULT_COURSE_OPTIONS } from "../../training-selection/training-defaults";
import "./steps.css";

/**
 * Training Selection step (Simulation + Training campaigns).
 *
 * The course grid and the "Configure Training" modal live in
 * component/training-selection/ so the AI/MCP chat picker renders exactly the
 * same UI; this step only owns the selection state and pushes the resulting
 * `training` object up to the wizard.
 */
export default class TrainingSelectionStep extends Component {
	constructor(props) {
		super(props);
		const t = props.training || {};
		this.state = {
			// selection
			trainingAssignmentMode: t.trainingAssignmentMode || "single",
			selectedCourses: Array.isArray(t.selectedCourses) ? [...t.selectedCourses] : [],
			options: t.selectedOptions ? { ...t.selectedOptions } : {},
			// course metadata captured from the catalog (name/logo lookup)
			courseName: t.courseName ? { ...t.courseName } : {},
			imageURL: t.imageURL || "",
			// Configure Training modal
			showConfigureModal: false,
			configureTab: "course",
			emailTemplateTabVisited: false,
			// email templates
			sendCourseTemplateId: t.sendCourseTemplateId || "",
			courseCompletionTemplateId: t.courseCompletionTemplateId || "",
			courseQuizFailReminderTemplateId: t.courseQuizFailReminderTemplateId || "",
		};
	}

	handleCoursesLoaded = ({ courseName, imageURL }) => {
		this.setState((prev) => ({
			courseName: { ...prev.courseName, ...courseName },
			imageURL: imageURL || prev.imageURL,
		}));
	};

	handleModeChange = (mode) => {
		this.setState((prev) => {
			let selectedCourses = prev.selectedCourses;
			if (mode === "single" && selectedCourses.length > 1) selectedCourses = [selectedCourses[0]];
			return { trainingAssignmentMode: mode, selectedCourses };
		}, this.pushUpdate);
	};

	handleCourseToggle = (courseId) => {
		this.setState((prev) => {
			const selected = [...prev.selectedCourses];
			const idx = selected.indexOf(courseId);
			if (idx > -1) {
				selected.splice(idx, 1);
			} else if (prev.trainingAssignmentMode === "single") {
				return { selectedCourses: [courseId] };
			} else {
				selected.push(courseId);
			}
			return { selectedCourses: selected };
		}, this.pushUpdate);
	};

	handleCourseRemove = (courseId) => {
		this.setState((prev) => ({
			selectedCourses: prev.selectedCourses.filter((id) => id !== courseId),
		}), this.pushUpdate);
	};

	setCourseOptions = (courseId, settings) => {
		this.setState((prev) => ({ options: { ...prev.options, [courseId]: settings } }), this.pushUpdate);
	};

	setTemplateId = (key, value) => this.setState({ [key]: value }, this.pushUpdate);

	openConfigureModal = () => {
		if (!this.state.selectedCourses.length) {
			toast.error("Please select at least one training course first");
			return;
		}
		this.setState({ showConfigureModal: true, configureTab: "course" });
	};

	/** Every selected course must have its per-course settings configured. */
	allCoursesConfigured = () =>
		this.state.selectedCourses.length > 0 &&
		this.state.selectedCourses.every((id) => !!this.state.options[id]);

	/** Training is only "complete" once every course is configured AND the
		admin has reviewed/selected the email templates. */
	isTrainingFullyConfigured = () => this.allCoursesConfigured() && this.state.emailTemplateTabVisited;

	/** Modal saved: hand the selection up and move the wizard on. */
	handleModalSave = () => {
		this.pushUpdate();
		this.setState({ showConfigureModal: false });
		if (this.props.onProceed) this.props.onProceed();
	};

	/** Build the training object and hand it to the wizard.
		Only genuinely-configured courses go into `selectedOptions` here — filling in
		DEFAULT_COURSE_OPTIONS for the rest (like buildSelectedOptions does) would make
		this step, on remount, treat a merely-*selected* course as already configured
		(the wizard round-trips this object back as `props.training`, and the
		constructor below seeds `options` straight from `selectedOptions`), so
		"Configure Setting" would wrongly show "Configured" without the user ever
		having opened it. The wizard's own buildPayload() fills in the defaults for
		any course left unconfigured at launch time instead. */
	pushUpdate = () => {
		if (!this.props.onUpdate) return;
		this.props.onUpdate({
			trainingAssignmentMode: this.state.trainingAssignmentMode,
			selectedCourses: [...this.state.selectedCourses],
			selectedOptions: { ...this.state.options },
			courseName: this.state.courseName,
			imageURL: this.state.imageURL,
			sendCourseTemplateId: this.state.sendCourseTemplateId || null,
			courseCompletionTemplateId: this.state.courseCompletionTemplateId || null,
			courseQuizFailReminderTemplateId: this.state.courseQuizFailReminderTemplateId || null,
			sendQuizTemplateId: null,
		});
	};

	validate = () => {
		if (!this.state.selectedCourses.length) {
			toast.error("Please select at least one training course");
			return false;
		}
		if (this.state.trainingAssignmentMode === "single" && this.state.selectedCourses.length > 1) {
			toast.error("Single mode allows only one training course");
			return false;
		}
		// Clicking the wizard's Continue opens the Configure Training modal and
		// requires course settings + email templates before advancing.
		if (!this.isTrainingFullyConfigured()) {
			this.setState({
				showConfigureModal: true,
				configureTab: this.allCoursesConfigured() ? "email" : "course",
				...(this.allCoursesConfigured() ? { emailTemplateTabVisited: true } : {}),
			});
			return false;
		}
		this.pushUpdate();
		return true;
	};

	render() {
		const { selectedCourses, trainingAssignmentMode } = this.state;

		return (
			<div className="step-content-wrapper">
				<div className="step-header">
					<h4>Training Selection</h4>
					<p>Choose a training course to initiate your simulation.</p>
				</div>

				<CourseCatalog
					selectedCourses={selectedCourses}
					trainingAssignmentMode={trainingAssignmentMode}
					onModeChange={this.handleModeChange}
					onToggleCourse={this.handleCourseToggle}
					onCoursesLoaded={this.handleCoursesLoaded}
				/>

				<ConfigureTrainingModal
					show={this.state.showConfigureModal}
					onHide={() => this.setState({ showConfigureModal: false })}
					initialTab={this.state.configureTab}
					selectedCourses={selectedCourses}
					options={this.state.options}
					courseName={this.state.courseName}
					imageURL={this.state.imageURL}
					templateIds={{
						sendCourseTemplateId: this.state.sendCourseTemplateId,
						courseCompletionTemplateId: this.state.courseCompletionTemplateId,
						courseQuizFailReminderTemplateId: this.state.courseQuizFailReminderTemplateId,
					}}
					emailTemplateTabVisited={this.state.emailTemplateTabVisited}
					onEmailTabVisited={() => this.setState({ emailTemplateTabVisited: true })}
					onTemplateIdChange={this.setTemplateId}
					onRemoveCourse={this.handleCourseRemove}
					onSetCourseOptions={this.setCourseOptions}
					onSave={this.handleModalSave}
				/>
			</div>
		);
	}
}

export { DEFAULT_COURSE_OPTIONS };
